/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { GistPayload } from 'cio/utils/in-app/gist';
import { isBlank } from '@ember/utils';
import { MessageDisplay } from 'cio/app-models/in-app/in-app-message';
import {
  inAppMessageDeliveryPriorityLabel,
  InAppPlatform,
  ModalPosition,
  OverlayPosition,
} from 'cio/utils/in-app/message-config';
import moment from 'moment-timezone';

interface InAppConfigDetailsSignature {
  Args: {
    gistPayload: GistPayload;
  };
  Element: HTMLDivElement;
}

interface PageRule {
  operator: 'equals' | 'contains';
  value: string;
  platform: InAppPlatform;
  icon: string;
}

const DO_NOT_DISPLAY_REGEX = '^DO_NOT_DISPLAY_IN_APP$';

export default class InAppConfigDetails extends Component<InAppConfigDetailsSignature> {
  get messageConfig() {
    let { gistPayload } = this.args;
    let { gist } = gistPayload.properties;
    let expiration;
    if (!isBlank(gistPayload?.expiry)) {
      expiration = moment(gistPayload.expiry).unix();
    }

    return {
      priority: inAppMessageDeliveryPriorityLabel(gistPayload?.priority) ?? '',
      display: this.toDisplay(gist?.position, gist?.elementId, gist?.overlayColor, gist?.messageWidth),
      expiration,
      pageRules: [
        ...this.pageRuleFromRegex('android', gist?.routeRuleAndroid),
        ...this.pageRuleFromRegex('ios', gist?.routeRuleApple),
        ...this.pageRuleFromRegex('web', gist?.routeRuleWeb),
      ],
    };
  }

  private toDisplay(position?: string, elementId?: string, overlayColor?: string, messageWidth?: number) {
    if (position == null && elementId == null) {
      // default
      return MessageDisplay.default();
    }
    let display: MessageDisplay;
    if (position != null) {
      display = MessageDisplay.modal(position as ModalPosition, overlayColor, messageWidth);
    } else if (elementId?.startsWith('x-gist-') === true) {
      display = MessageDisplay.overlay(elementId as OverlayPosition, messageWidth);
    } else {
      display = MessageDisplay.inline(elementId ?? '');
    }
    return display;
  }

  private pageRuleFromRegex(platform: InAppPlatform, regexStr?: string): PageRule[] {
    if (regexStr == null || regexStr === DO_NOT_DISPLAY_REGEX) {
      return [];
    }

    // Trim the start and end regex boundaries
    let trimmedStr = regexStr.replace(/^\^/, '').replace(/\$$/, '');

    // Split the regex string by '|'
    let ruleStrs = trimmedStr.split('|');

    let rules: PageRule[] = [];

    ruleStrs.forEach((ruleStr) => {
      // Removing surrounding parentheses
      ruleStr = ruleStr.replace(/^\(/, '').replace(/\)$/, '');

      // Convert back from regex to original value
      let value = this.revertFromRegex(ruleStr);

      let operator: PageRule['operator'];

      // Determine operator
      if (ruleStr.includes('.*')) {
        operator = 'contains';
      } else {
        operator = 'equals';
      }

      rules.push({
        operator,
        value,
        platform,
        icon: platform === 'web' ? 'desktop' : platform === 'ios' ? 'apple' : 'android',
      });
    });

    return rules;
  }

  private revertFromRegex(value: string): string {
    // Convert all regex wildcards to the original '*' wildcard
    value = value.replace(/\.\*/g, '*');

    // Remove redundant '*' wildcards from the beginning and end only if there are other characters in the string
    if (value.length > 1) {
      value = value.replace(/^\*/, '').replace(/\*$/, '');
    }

    return value;
  }
}
